import type {
  DerivedAggregates,
  GlobalClimate,
  WorldState,
} from "../engine/types";
import { JournalDrawer } from "./JournalDrawer";
import { EuropeanProceduresDrawer } from "./EuropeanProceduresDrawer";
import { OrnamentBridge } from "./Ornaments";
import {
  DiplomatieArticle,
  EconomieArticle,
  SocieteArticle,
} from "./SectionArticles";
import { WorldMap } from "./WorldMap";

interface Props {
  state: WorldState;
  aggregates: DerivedAggregates;
}

/** Étiquette qualitative à partir du score politique (0..100). */
function relationLabel(score: number): string {
  if (score >= 70) return "Allié";
  if (score >= 55) return "Cordial";
  if (score >= 40) return "Tendu";
  if (score >= 25) return "Hostile";
  return "Rupture";
}

function relationColor(score: number): string {
  if (score >= 55) return "var(--color-blue)";
  if (score >= 40) return "var(--color-ink)";
  if (score >= 25) return "var(--color-ochre)";
  return "var(--color-red)";
}

/**
 * M4 — Baseline des relations politiques à T0 par pays canonique.
 * Mirroir des valeurs initialisées dans `Geopolitics::with_canonical_world()`
 * (Rust). Source d'unicité côté front pour calculer les deltas affichés.
 * Si Rust dérive ses valeurs initiales, mettre à jour cette table.
 */
const BASELINE_POLITICAL_RELATION: Record<string, number> = {
  "République de l'Aigle": 55,
  "Union du Blé": 78,
  "Le Tsarat": 25,
  "Le Dragon Rouge": 30,
  "L'Émirat du Soleil": 45,
  "Confédération Alpine": 60,
  "Fédération Tropicale": 50,
};

function deltaSign(delta: number): string {
  if (delta > 0.5) return `+${Math.round(delta)}`;
  if (delta < -0.5) return `${Math.round(delta)}`;
  return "0";
}

const CLIMATE_LABEL: Record<GlobalClimate, string> = {
  calm: "Climat international apaisé",
  tense: "Tensions diplomatiques",
  cold_war: "Guerre froide ravivée",
  open_conflict: "Conflit ouvert en cours",
};

const CLIMATE_COLOR: Record<GlobalClimate, string> = {
  calm: "var(--color-blue)",
  tense: "var(--color-ink)",
  cold_war: "var(--color-ochre)",
  open_conflict: "var(--color-red)",
};

function ClimatBadge({ climate }: { climate: GlobalClimate }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-num)",
        fontSize: "var(--type-caption)",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: CLIMATE_COLOR[climate],
        marginBottom: "8px",
      }}
    >
      {CLIMATE_LABEL[climate]}
    </div>
  );
}

export function DrawerPanels({ state, aggregates }: Props) {
  // G4 — onboarding : les drawers ne sont pas tous accessibles d'emblée.
  // Au tour 0 le joueur voit un cockpit minimaliste ; les volets se révèlent
  // au fil du mandat (cf. project_retention_doctrine, levier #4 + onboarding).
  const turn = state.turn;
  const showSociete = turn >= 1;
  const showEconomie = turn >= 2;

  return (
    <aside className="panels" aria-label="Où va le pays ?">
      <div className="rule-dotted section-rubric-rule" aria-hidden="true" />
      <header className="section-rubric">
        <OrnamentBridge size={40} className="section-vignette" />
        <h2 className="section-rubric-title">Où va le pays&nbsp;?</h2>
      </header>
      {showSociete && (
        <details className="drawer drawer-appear">
          <summary>Société</summary>
          <div className="drawer-body">
            <SocieteArticle state={state} aggregates={aggregates} />
          </div>
        </details>
      )}

      {showEconomie && (
        <details className="drawer drawer-appear" open>
          <summary>Économie</summary>
          <div className="drawer-body">
            <EconomieArticle state={state} aggregates={aggregates} />
          </div>
        </details>
      )}

      {turn >= 2 && <JournalDrawer />}

      {turn >= 4 && (
        <details className="drawer drawer-appear" open>
          <summary>Diplomatie</summary>
          <div className="drawer-body">
            <ClimatBadge climate={state.geopolitics.global_climate} />
            <WorldMap state={state} />
            <DiplomatieArticle state={state} aggregates={aggregates} />
            <div className="drawer-subtitle">Pays connus</div>
            {state.geopolitics.countries
              .filter((c) => c.known_to_player)
              .map((c) => {
                const baseline = BASELINE_POLITICAL_RELATION[c.label];
                const delta =
                  baseline !== undefined ? c.political_relation - baseline : 0;
                const trend =
                  delta > 0.5 ? "up" : delta < -0.5 ? "down" : "flat";
                return (
                  <div key={c.label} className="kv country-row">
                    <span>{c.label}</span>
                    <span className="country-meta">
                      <span
                        style={{ color: relationColor(c.political_relation) }}
                      >
                        {relationLabel(c.political_relation)}
                      </span>
                      {baseline !== undefined && trend !== "flat" && (
                        <span className="country-delta" data-trend={trend}>
                          {deltaSign(delta)}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            {state.geopolitics.countries.filter((c) => !c.known_to_player)
              .length > 0 && (
              <p
                style={{
                  fontSize: "var(--type-caption)",
                  color: "var(--color-grey-2)",
                  fontStyle: "italic",
                  marginTop: "8px",
                }}
              >
                {
                  state.geopolitics.countries.filter((c) => !c.known_to_player)
                    .length
                }{" "}
                pays restent à découvrir au fil de votre mandat.
              </p>
            )}
          </div>
        </details>
      )}

      {/* K1 + L+3 — Acteurs nommés. Affiche les éveillés ET la
          progression des latents avec une jauge relevance/5.0. */}
      {state.polity.actors && state.polity.actors.length > 0 && (
        <details className="drawer drawer-appear">
          <summary>Acteurs en scène</summary>
          <div className="drawer-body">
            {state.polity.actors
              .filter((a) => a.known_to_player)
              .map((a) => (
                <div key={a.id} className="actor-row">
                  <div className="actor-line">
                    <span className="actor-label">{a.label}</span>
                    <span
                      className="actor-relation"
                      data-relation={
                        a.relation_to_player > 5
                          ? "ally"
                          : a.relation_to_player < -5
                            ? "hostile"
                            : "neutral"
                      }
                    >
                      {a.relation_to_player > 5
                        ? "Allié"
                        : a.relation_to_player < -5
                          ? "Hostile"
                          : "Neutre"}
                    </span>
                  </div>
                </div>
              ))}
            {state.polity.actors.filter((a) => !a.known_to_player).length >
              0 && (
              <>
                <div className="drawer-subtitle">En observation</div>
                {state.polity.actors
                  .filter((a) => !a.known_to_player && a.relevance > 0.5)
                  .sort((a, b) => b.relevance - a.relevance)
                  .map((a) => {
                    const pct = Math.min(100, (a.relevance / 5) * 100);
                    return (
                      <div key={a.id} className="actor-row actor-latent">
                        <div className="actor-line">
                          <span className="actor-label-faded">{a.label}</span>
                          <span className="actor-relevance">
                            {a.relevance.toFixed(1)}/5
                          </span>
                        </div>
                        <div className="actor-gauge" aria-hidden="true">
                          <div
                            className="actor-gauge-fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </>
            )}
          </div>
        </details>
      )}

      {/* Europe S2 — drawer Bruxelles, visible seulement si une procédure
          européenne est active dans le mandat. */}
      {state.polity.european_interventions &&
        state.polity.european_interventions.length > 0 && (
          <EuropeanProceduresDrawer
            interventions={state.polity.european_interventions}
            currentTurn={state.turn}
          />
        )}

      {/* Sprint Cohortes émergentes — groupes sociaux identifiés. */}
      {state.polity.revealed_cohort_groups &&
        state.polity.revealed_cohort_groups.length > 0 && (
          <details className="drawer drawer-appear">
            <summary>
              Groupes sociaux{" "}
              <span className="drawer-count">
                · {state.polity.revealed_cohort_groups.length}
              </span>
            </summary>
            <div className="drawer-body">
              {state.polity.revealed_cohort_groups
                .slice()
                .sort((a, b) => b.population_share_pct - a.population_share_pct)
                .map((g) => {
                  const moodTrend =
                    g.current_satisfaction < 35
                      ? "low"
                      : g.current_satisfaction > 60
                        ? "high"
                        : "mid";
                  const moodLabel =
                    moodTrend === "low"
                      ? "en colère"
                      : moodTrend === "high"
                        ? "satisfait"
                        : "ambivalent";
                  return (
                    <div key={g.id} className="cohort-group-row">
                      <div className="cohort-group-line">
                        <span className="cohort-group-label">{g.label}</span>
                        <span
                          className="cohort-group-mood"
                          data-mood={moodTrend}
                        >
                          {moodLabel}
                        </span>
                      </div>
                      <div className="cohort-group-meta">
                        révélé T{g.revealed_at_turn} ·{" "}
                        {g.population_share_pct.toFixed(1)} % de la population ·
                        satisfaction {Math.round(g.current_satisfaction)} %
                      </div>
                    </div>
                  );
                })}
            </div>
          </details>
        )}

      {state.polity.regime !== "democracy" && (
        <details className="drawer drawer-appear" open>
          <summary>Presse étrangère</summary>
          <div className="drawer-body">
            <ForeignPressPanel state={state} aggregates={aggregates} />
          </div>
        </details>
      )}

      {turn >= 8 && (
        <details
          className="drawer drawer-appear"
          open={!!state.polity.authoritarian_arc}
        >
          <summary>Scénarios actifs</summary>
          <div className="drawer-body">
            {state.polity.authoritarian_arc ? (
              <AuthoritarianArcCard
                arc={state.polity.authoritarian_arc}
                turn={turn}
              />
            ) : (
              <p
                style={{
                  fontSize: "var(--type-caption)",
                  color: "var(--color-grey-2)",
                }}
              >
                Aucun scénario structurel déclenché pour l'instant.
              </p>
            )}
          </div>
        </details>
      )}
    </aside>
  );
}

/** S18 — carte de l'arc « Dérive autoritaire » (premier scénario câblé). */
const ARC_STAGES: Record<string, { label: string; desc: string }> = {
  suspicion: {
    label: "SUSPICION",
    desc: "Les chancelleries s'inquiètent de la concentration du pouvoir. Rien d'irréversible — pour l'instant.",
  },
  tournant: {
    label: "LE TOURNANT",
    desc: "L'état d'exception est déclaré. La République a changé de nature.",
  },
  verrouillage: {
    label: "VERROUILLAGE",
    desc: "Presse sous pression, sondages officiels, premières sanctions internationales.",
  },
  installe: {
    label: "RÉGIME INSTALLÉ",
    desc: "La rue est le dernier contre-pouvoir. Un effondrement durable du réel emporterait le régime.",
  },
};

function AuthoritarianArcCard({
  arc,
  turn,
}: {
  arc: NonNullable<WorldState["polity"]["authoritarian_arc"]>;
  turn: number;
}) {
  const stage = ARC_STAGES[arc.stage] ?? ARC_STAGES.suspicion;
  return (
    <div className="scenario-arc" data-stage={arc.stage}>
      <div className="scenario-arc-kicker">
        DÉRIVE AUTORITAIRE · depuis le mois {arc.entered_at_turn}
      </div>
      <div className="scenario-arc-stage">{stage.label}</div>
      <p className="scenario-arc-desc">{stage.desc}</p>
      <div className="scenario-arc-meta">
        Stage entamé au mois {arc.stage_entered_at} · mois courant {turn}
      </div>
    </div>
  );
}

/** S18 Lot B — en dictature, les sondages officiels sont truqués : la
 *  presse étrangère devient le seul thermomètre du réel (doctrine « le
 *  pouvoir corrompt la mesure »). */
const FOREIGN_PRESS_BY_STAGE: Record<string, string[]> = {
  tournant: [
    "« L'Hexagone bascule » — les rédactions étrangères dépêchent leurs envoyés spéciaux.",
    "Premières ONG interdites de séjour. Les correspondants travaillent depuis Bruxelles.",
  ],
  verrouillage: [
    "« Les chiffres officiels ne veulent plus rien dire », écrit le correspondant du Times.",
    "Les capitales occidentales rappellent leurs ambassadeurs pour consultation.",
  ],
  installe: [
    "La presse en exil publie chaque semaine son estimation du « réel » hexagonal.",
    "« Un pays coupé en deux : les sondages du régime, et la rue. »",
  ],
};

function ForeignPressPanel({
  state,
  aggregates,
}: {
  state: WorldState;
  aggregates: DerivedAggregates;
}) {
  const stage = state.polity.authoritarian_arc?.stage ?? "installe";
  const quotes =
    FOREIGN_PRESS_BY_STAGE[stage] ?? FOREIGN_PRESS_BY_STAGE.installe;
  const official = state.polity.approval_rate;
  const real = aggregates.meanSatisfaction;
  return (
    <div className="foreign-press">
      {quotes.map((q, i) => (
        <p key={i} className="foreign-press-quote">
          {q}
        </p>
      ))}
      <div className="foreign-press-gap">
        <span>
          Approbation officielle :{" "}
          <strong>
            {official != null ? `${Math.round(official)} %` : "—"}
          </strong>
        </span>
        <span>
          Estimation presse étrangère : <strong>{Math.round(real)} %</strong>
        </span>
      </div>
      <p className="foreign-press-note">
        Les instituts nationaux ne publient plus que des chiffres officiels.
        Seule la presse étrangère estime encore le pays réel.
      </p>
    </div>
  );
}
